import React, {
    forwardRef,
    KeyboardEventHandler,
    MouseEventHandler,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from "react";
import classnames from "classnames";
import {ReactComponent as Play} from "../../../../assets/svgs/video/play.svg";
import {ReactComponent as Pause} from "../../../../assets/svgs/video/pause.svg";
import {ReactComponent as Unmute} from "../../../../assets/svgs/video/unmute.svg";
import {ReactComponent as Mute} from "../../../../assets/svgs/video/mute.svg";
import {ReactComponent as Fullscreen} from "../../../../assets/svgs/video/full-screen.svg";
import {Box, Fade, Theme, Typography} from "@mui/material";
import type {VideoPlayerRef, VideoPlayerTypes} from "../index";
import {Utils} from "../../../../core";

export interface NativeVideoPlayerProps {
    id: string,
    src: string,
    noControls: boolean,
    title: string,
    autoPlay: boolean,
    muted: boolean,
    type: VideoPlayerTypes,
    className?: string,
}

interface NativeVideoPlayerState {
    paused: boolean,
    duration: number,
    currentTime: number,
    muted: boolean,
    loaded: boolean,
}

const NativeVideoPlayer = forwardRef<VideoPlayerRef, NativeVideoPlayerProps>((props, ref) => {
    const [state, setState] = useState<NativeVideoPlayerState>({
        paused: true,
        duration: -1,
        currentTime: 0,
        muted: props.muted,
        loaded: false,
    });
    const player = useRef<HTMLVideoElement>(null);

    const videoSourceType = useMemo(() =>
            `video/${props.src ? props.src.split('.')[props.src.split('.')?.length - 1] : ''}`,
        [props.src]);


    /**
     * Plays the current video of the player
     */
    const play = useCallback<AsyncVoidCallback>(async () => {
        try {
            await player.current?.play();
            setState(prevState => ({
                ...prevState,
                progress: player.current?.currentTime ?? 0,
                paused: false,
            }))
        } catch {
        }
    }, []);

    /**
     * Pauses the current video of the player
     */
    const pause = useCallback<VoidCallback>(() => {
        player.current?.pause();
        setState(prevState => ({...prevState, paused: true}));
    }, []);

    /**
     * Mutes the video of the player
     */
    const mute = useCallback<VoidCallback>(() => {
        setState(prevState => ({...prevState, muted: true}));
        if (player.current) {
            player.current.muted = true;
        }
    }, []);

    /**
     * Un-Mutes the video of the player
     */
    const unmute = useCallback<VoidCallback>(() => {
        setState(prevState => ({...prevState, muted: false}));
        if (player.current) {
            player.current.muted = false;
        }
    }, []);

    /**
     * Makes the video of the player fullscreen
     */
    const fullscreen = useCallback<VoidCallback>(() => {
        const videoElement = player.current;
        if (!videoElement)
            return;
        const requestFullScreen = videoElement.requestFullscreen || videoElement.mozRequestFullScreen || videoElement.webkitRequestFullScreen;
        if (requestFullScreen) {
            requestFullScreen.bind(videoElement)();
        }
    }, [])

    /**
     * Returns the current time of the video
     */
    const timestamp = useCallback<VoidCallback>(() => player.current?.currentTime ?? 0, [])

    /**
     * Seeks the video to the given time
     */
    const seek = useCallback<(t: number) => void>((time) => player.current?.fastSeek(time), []);

    /**
     * Attaches the video player functionalities to the given ref so that the parent components can directly use them.
     */
    useImperativeHandle(ref, () => ({
        pause,
        play,
        mute,
        unmute,
        fullscreen,
        timestamp,
        seek,
        getPlayerInstance: () => [false, player.current],
    }) as VideoPlayerRef, [pause, play, mute, unmute, fullscreen, timestamp, seek])

    /**
     * Updates the player duration and current time
     */
    const updatePlayerTime = useCallback<VoidCallback>(() => {
        setState(prevState => ({
            ...prevState,
            currentTime: player.current?.currentTime ?? 0,
            duration: player.current?.duration ?? -1,
        }))
    }, []);

    /**
     * Listens to the changes in the paused state and with each change:
     * if the state is playing, then creates an interval that will track the progression of the time and video's
     * duration.
     * Removes the interval before the next call of this listener.
     */
    useEffect(() => {
        if (!player.current) return;
        const _player = player.current;
        _player.addEventListener('timeupdate', updatePlayerTime)
        _player.addEventListener('durationchange', updatePlayerTime)
        return () => {
            _player.removeEventListener('timeupdate', updatePlayerTime)
            _player.removeEventListener('durationchange', updatePlayerTime)
        }
    }, [updatePlayerTime])

    /**
     * Updates the current time of the video player
     * @param value     The value to be added to the current time
     */
    const changeCurrentTime = useCallback<(t: number) => void>((value: number) => {
        if (!player.current) return;
        player.current.currentTime += value;
    }, []);

    /**
     * Either plays or pauses the video
     */
    const changePlayerState = useCallback<MouseEventHandler<HTMLVideoElement>>((event) => {
        if (event && event.detail !== 1) {
            if (state.paused) {
                play().then();
            } else {
                pause();
            }
        }
        if (state.paused) {
            play().then();
        } else {
            pause();
        }
    }, [pause, play, state.paused]);

    /**
     * Handles the key presses for this video component.
     * @param event     The key event
     */
    const onKeyUp = useCallback<KeyboardEventHandler<HTMLDivElement>>((event) => {
        switch (event.key) {
            case "ArrowLeft":
                changeCurrentTime(-5);
                break;
            case "ArrowRight":
                changeCurrentTime(5);
                break;
            case " ":
                event.preventDefault();
                event.stopPropagation();
                changePlayerState(void 0 as any);
                break;
            default:
                break;
        }
    }, [changeCurrentTime, changePlayerState])

    /**
     * Sets the current time of the player with respect to the clicked portion of the video
     * @param event    The mouse event
     */
    const onProgressClicked = useCallback<MouseEventHandler<HTMLProgressElement>>((event) => {
        const boundingRect = (event.target as HTMLProgressElement).getBoundingClientRect();
        const percentage = (event.clientX - boundingRect.left) / boundingRect.width
        if (player.current) {
            player.current.currentTime = player.current.duration * percentage;
        }
    }, []);

    /**
     * The styles of the component.
     */
    const sx = useCallback<ThemeMixin<Theme>>((theme) => ({
        '& .progress': {
            color: theme.palette.primary.main,
            accentColor: theme.palette.primary.main,
            '&::-webkit-progress-bar': {
                backgroundColor: theme.palette.common.white,
            },
            '&::-webkit-progress-inner-element': {
                backgroundColor: theme.palette.common.white,
            },
            '&::-webkit-progress-value': {
                backgroundColor: theme.palette.primary.main,
            },
            '&::-moz-progress-bar': {
                backgroundColor: theme.palette.primary.main,
            },
        },
    }), [])

    return (
        <>
            <Box
                sx={sx}
                className={classnames('video-container-layout', props.className, {'active': state.duration !== -1, [props.type]: true})}
                onKeyUp={onKeyUp}
                onKeyDown={Utils.modifyEvent}
                tabIndex={-1}
            >
                <div className={'video-container'}>
                    <Fade in={state.duration === -1 && !props.noControls}>
                        <div className={'play-button-container'}>
                            <button className={'play-button'} onClick={play}>
                                <Play/>
                            </button>
                        </div>
                    </Fade>
                    <video
                        onClick={changePlayerState}
                        onDoubleClick={fullscreen}
                        controls={false}
                        autoPlay={props.autoPlay}
                        playsInline={props.autoPlay}
                        muted={props.muted}
                        ref={player}
                        title={props.title}
                        id={props.id}
                        preload={'metadata'}
                    >
                        <source src={props.src} type={videoSourceType}/>
                        <Typography component={'p'} variant={'caption'} textAlign={'center'}>
                            unable to play the current format
                        </Typography>
                    </video>
                </div>
                <Fade in={state.duration !== -1 && !props.noControls} unmountOnExit mountOnEnter>
                    <div className={classnames('video-controls', {[props.type]: true})}>
                        <div className={classnames('inner')}>
                            {
                                state.paused
                                    ? <div className={'video-icon-container'}><Play className={'video-icon'} onClick={play}/></div>
                                    : <div className={'video-icon-container'}><Pause className={'video-icon'} onClick={pause}/></div>
                            }
                            <progress
                                className={'progress'}
                                value={state.currentTime.toString()}
                                max={state.duration}
                                onClick={onProgressClicked}
                            />
                            {
                                state.muted
                                    ? <div className={'video-icon-container'}><Unmute className={'video-icon'} onClick={unmute}/></div>
                                    : <div className={'video-icon-container'}><Mute className={'video-icon'} onClick={mute}/></div>
                            }
                            <div className={'video-icon-container'}><Fullscreen className={'video-icon'} onClick={fullscreen}/></div>
                        </div>

                    </div>
                </Fade>
            </Box>
        </>
    )
});

export default NativeVideoPlayer;
